import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { apiPost, apiPut } from "helpers/apiService";

const CRAFT_EDITOR_BASE = "/craft-landing-editor/index.html";
const LANDING_PAGES_INDEX_KEY = "landing-pages-index";
const contentKey = pageId => `landing-page-content-${pageId}`;

const isCraftSerializedContent = value => {
  if (typeof value !== "string" || !value.trim()) return false;
  const trimmed = value.trim();
  if (trimmed === "null" || trimmed === "undefined") return false;
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return false;
  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== "object") return false;
    // Craft serialize is usually a map of nodes keyed by id, including ROOT.
    if (parsed.ROOT || parsed.root) return true;
    const values = Object.values(parsed);
    return values.some(
      node =>
        node &&
        typeof node === "object" &&
        (node.type || node.displayName || node.props)
    );
  } catch (error) {
    return false;
  }
};

const ensurePageIndexEntry = ({ pageId, name, subtitle }) => {
  try {
    const rawIndex = localStorage.getItem(LANDING_PAGES_INDEX_KEY);
    let index = [];
    try {
      index = rawIndex ? JSON.parse(rawIndex) : [];
    } catch (error) {
      index = [];
    }

    const nextPage = {
      id: pageId,
      name: name || "Untitled Page",
      subtitle: subtitle || "Master landing template",
      thumbnail:
        "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80",
      updatedAt: new Date().toISOString(),
    };

    const existingIdx = Array.isArray(index)
      ? index.findIndex(item => item?.id === pageId)
      : -1;
    const nextIndex =
      existingIdx >= 0
        ? index.map((item, idx) =>
            idx === existingIdx ? { ...item, ...nextPage } : item
          )
        : [nextPage, ...(Array.isArray(index) ? index : [])];

    localStorage.setItem(LANDING_PAGES_INDEX_KEY, JSON.stringify(nextIndex));
  } catch (error) {
    console.error("Failed to update craft page index:", error);
  }
};

const seedEditorContent = ({ pageId, name, subtitle, templateContent }) => {
  ensurePageIndexEntry({ pageId, name, subtitle });

  if (isCraftSerializedContent(templateContent)) {
    localStorage.setItem(contentKey(pageId), templateContent.trim());
    return true;
  }

  // No valid saved Craft JSON — start from editor default layout.
  localStorage.removeItem(contentKey(pageId));
  return false;
};

/**
 * Hosts the Craft landing-page editor in an iframe.
 * Create: ?create=1 (blank/default layout)
 * Edit: ?pageId=master-{id} with API templateContent seeded into localStorage
 */
const CraftEditorHost = ({
  autoCreate = false,
  pageId,
  syncToApi = "master",
  templateId = null,
  templateTitle = "",
  templateDescription = "",
  templateContent = "",
}) => {
  const iframeRef = useRef(null);
  const navigate = useNavigate();
  const [editorReady, setEditorReady] = useState(false);

  const resolvedPageId = useMemo(() => {
    if (pageId) return String(pageId);
    if (templateId) return `master-${templateId}`;
    return null;
  }, [pageId, templateId]);

  useEffect(() => {
    setEditorReady(false);

    if (!autoCreate && resolvedPageId) {
      seedEditorContent({
        pageId: resolvedPageId,
        name: templateTitle || "Untitled Page",
        subtitle: templateDescription || "Master landing template",
        templateContent,
      });
    }

    const timer = setTimeout(() => setEditorReady(true), 40);
    return () => clearTimeout(timer);
  }, [
    autoCreate,
    resolvedPageId,
    templateContent,
    templateDescription,
    templateTitle,
  ]);

  const getEditorSrc = () => {
    const params = new URLSearchParams();

    if (autoCreate || !resolvedPageId) {
      params.set("create", "1");
    } else {
      params.set("pageId", resolvedPageId);
    }

    const query = params.toString();
    return query ? `${CRAFT_EDITOR_BASE}?${query}` : CRAFT_EDITOR_BASE;
  };

  const handleMessage = useCallback(
    event => {
      if (event.origin !== window.location.origin) return;

      if (event.data?.type === "CRAFT_LANDING_READY") {
        if (!autoCreate && resolvedPageId && iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage(
            { type: "CRAFT_LANDING_OPEN", pageId: resolvedPageId },
            window.location.origin
          );
        }
        return;
      }

      if (event.data?.type !== "CRAFT_LANDING_SAVE") return;
      if (!syncToApi) return;

      const { pageName, templateContent: craftContent, templateCSS } =
        event.data;

      if (syncToApi === "master") {
        const payload = {
          // Prefer the stored template name/description so rename is not overwritten
          // by the Craft editor's internal page name.
          templateTitle: templateTitle || pageName || "Untitled Page",
          templateDescription: templateDescription || "",
          templateContent: craftContent || "",
          templateCSS: templateCSS || "",
          templateType: 1,
          publishStatusId: 0,
          isActive: true,
        };

        toast.loading(templateId ? "Updating template..." : "Saving template...");

        const request = templateId
          ? apiPut("ProjectMasterLandingPageTemplate", {
              ...payload,
              id: Number(templateId),
            })
          : apiPost("ProjectMasterLandingPageTemplate", payload);

        request
          .then(() => {
            // Keep local craft cache in sync with what we just saved.
            if (resolvedPageId && isCraftSerializedContent(craftContent)) {
              localStorage.setItem(contentKey(resolvedPageId), craftContent);
              ensurePageIndexEntry({
                pageId: resolvedPageId,
                name: payload.templateTitle,
                subtitle: payload.templateDescription,
              });
            }

            toast.dismiss();
            toast.success(
              templateId
                ? "Template updated successfully"
                : "Template saved to Realty Space"
            );
            navigate("/master-landing-page");
          })
          .catch(error => {
            toast.dismiss();
            toast.error(
              error?.response?.data?.validationErrors?.[0] ||
                error?.response?.data?.message ||
                (templateId
                  ? "Failed to update template"
                  : "Failed to save template")
            );
          });
      }
    },
    [
      autoCreate,
      navigate,
      resolvedPageId,
      syncToApi,
      templateDescription,
      templateId,
      templateTitle,
    ]
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleMessage]);

  return (
    <React.Fragment>
      <ToastContainer autoClose={2000} />
      <div className="page-content craft-landing-editor-page">
        <div className="craft-landing-editor-host">
          {!editorReady ? (
            <div className="p-4">Loading editor…</div>
          ) : (
            <iframe
              ref={iframeRef}
              title="Landing Page Editor"
              src={getEditorSrc()}
              allow="clipboard-read; clipboard-write"
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                display: "block",
                background: "#fff",
              }}
            />
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

CraftEditorHost.propTypes = {
  autoCreate: PropTypes.bool,
  pageId: PropTypes.string,
  syncToApi: PropTypes.oneOf(["master", null]),
  templateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  templateTitle: PropTypes.string,
  templateDescription: PropTypes.string,
  templateContent: PropTypes.string,
};

export default CraftEditorHost;
